import { dynamicConfigStringListIncludes } from '@atlaskit/tmp-editor-statsig/dynamic-config-value-contains';

/**
 * Statsig dynamic config for the Review moment's optional slices.
 *
 * The config holds a string list under its `value` key; a feature is enabled when
 * its token is present. It is scoped to the Review moment cohort
 * (`platform_editor_ai_review_moment`), so the tokens are unprefixed.
 *
 * Tokens:
 *   - `'show-diff-by-default'`
 *   - `'hide-view-changes'`
 *   - `'step-through'`
 *   - `'show-accepted-pill'`
 *   - `'diff-based-segmenting'`
 *
 * Semantics: an absent token (or unset / not-ready config) means the feature is
 * OFF. Rollout is opt-in via the config's `value` list.
 */
export const REVIEW_MOMENT_CONFIG = 'platform_editor_ai_review_moment_config';

export const REVIEW_MOMENT_TOKENS = {
	showDiffByDefault: 'show-diff-by-default',
	hideViewChanges: 'hide-view-changes',
	stepThrough: 'step-through',
	showAcceptedPill: 'show-accepted-pill',
	diffBasedSegmenting: 'diff-based-segmenting',
} as const;

/**
 * Whether the Review Moment opens with deletions shown by default.
 */
export function isReviewMomentShowDiffByDefaultEnabled(): boolean {
	return dynamicConfigStringListIncludes(
		REVIEW_MOMENT_CONFIG,
		REVIEW_MOMENT_TOKENS.showDiffByDefault,
	);
}

/**
 * Whether the step-through experience is available in the Review Moment.
 */
export function isReviewMomentStepThroughEnabled(): boolean {
	return dynamicConfigStringListIncludes(REVIEW_MOMENT_CONFIG, REVIEW_MOMENT_TOKENS.stepThrough);
}

/**
 * Whether the Review Moment shows the "Accepted" confirmation pills — the full
 * `AcceptedToast` (after accepting all changes) and the per-segment
 * `MiniAcceptedToast` (while stepping through). Default OFF (absent token = no
 * pills): accepting tears the Review moment down without a confirmation pill. [CCI-19074]
 */
export function isReviewMomentAcceptedPillEnabled(): boolean {
	return dynamicConfigStringListIncludes(
		REVIEW_MOMENT_CONFIG,
		REVIEW_MOMENT_TOKENS.showAcceptedPill,
	);
}

/**
 * Whether Post Stream Review splits a top-level change into finer diff-level
 * sub-segments (`diffSegments`) instead of segmenting at the top-level node
 * boundary only. [CCI-18182]
 */
export function isReviewMomentDiffBasedSegmentingEnabled(): boolean {
	return dynamicConfigStringListIncludes(
		REVIEW_MOMENT_CONFIG,
		REVIEW_MOMENT_TOKENS.diffBasedSegmenting,
	);
}

/**
 * Hide inline View changes and show generated content during review.
 * Chat keeps its toggle and reveals deleted text only for deletion-only results.
 */
export function isReviewMomentHideViewChangesEnabled(): boolean {
	return dynamicConfigStringListIncludes(
		REVIEW_MOMENT_CONFIG,
		REVIEW_MOMENT_TOKENS.hideViewChanges,
	);
}
